/**
 * Z-Index System
 * 
 * This file defines a consistent z-index system to prevent conflicts
 * between different UI components. Higher numbers appear on top.
 * 
 * Guidelines:
 * - Use these constants instead of arbitrary numbers
 * - Add new layers as needed, maintaining the hierarchy
 * - Avoid values above 10000 unless absolutely necessary
 */

export const Z_INDEX = {
  // Base layers (0-99)
  BASE: 0,
  DROPDOWN: 10,
  STICKY: 20,
  
  // Table layers (100-199)
  TABLE_CELL: 100,
  TABLE_HEADER: 110,
  TABLE_STICKY_COLUMN: 120,
  
  // Grid-specific layers
  GRID_HEADER: 14,
  GRID_HEADER_MESSAGE_SIDEBAR_OPEN: 1,
  FIXED_COLUMN: 14,
  FIXED_COLUMN_MESSAGE_SIDEBAR_OPEN: 0,
  FIXED_CELL: 7,
  STICKY_COLUMN: 8,
  STICKY_COLUMN_MESSAGE_SIDEBAR_OPEN: 1,
  MESSAGE_CELL_DEFAULT: 10,
  MESSAGE_CELL_MESSAGE_SIDEBAR_OPEN: 1,
  MESSAGE_CELL_ACTIVE: 1000,
  
  // Modal and overlay layers (200-299)
  MODAL_BACKDROP: 200,
  MODAL_CONTENT: 210,
  TOOLTIP: 220,
  
  // Sidebar layers (300-399)
  SIDEBAR_BACKDROP: 300,
  SIDEBAR_CONTENT: 310,
  
  // Message system layers (400-499)
  MESSAGE_TRIGGER: 400,
  MESSAGE_SIDEBAR_BACKDROP: 430, // Canonical value - matches globals.css
  MESSAGE_SIDEBAR_CONTENT: 600,
  
  // Table header when message sidebar is open (should be lower)
  TABLE_HEADER_OVERRIDE: 100,
  
  // Message sidebar close button (600-699)
  MESSAGE_SIDEBAR_CLOSE: 650,
  
  // Top-level overlays (700-799)
  LOADING_OVERLAY: 700,
  NOTIFICATION: 710,
  
  // Other UI elements
  FILTER_ICON: 4,
  CELL_CONTENT: 6,
  AUDIT_PANEL: 2,
  AUDIT_PANEL_BACKDROP: 100,
  AUDIT_PANEL_CONTENT: 1000,
  AUDIT_PANEL_OVERLAY: 999,
  AUDIT_PANEL_CLOSE: 1999,
  AUDIT_PANEL_HEADER: 2000,
  
  // Filter sidebar layers
  FILTER_SIDEBAR_OVERLAY: 1999,
  FILTER_SIDEBAR_CONTENT: 2000,

  // Messages inbox chat side panel — a modal layer opened from inside the messages inbox dialog, so it
  // sits above every other panel (audit / filter sidebar at 2000) rather than colliding with them.
  INBOX_CHAT_OVERLAY: 2100,
  INBOX_CHAT_CONTENT: 2101,

  // Emergency override (1000+)
  EMERGENCY_OVERRIDE: 1000,
} as const;

export type ZIndexKey = keyof typeof Z_INDEX;
